import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

// One press, one reveal.
//
// The press that opens a campaign's setup thread is the only place that knows the owner just
// asked for it, and it happens in the dock, far from the message list. This store carries that
// one fact across, and deliberately does not survive a reload: a thread the owner comes back to
// shows finished words rather than typing them out again.
//
// It notifies rather than being a plain flag, because the thread is often ALREADY on screen
// when the press lands — a second press, or a thread the last press left open. Nothing re-reads
// a silent flag in that case, so the words would simply sit there finished.
const pending = new Set<string>();
const listeners = new Set<() => void>();
let version = 0;

export const markSetupRevealPending = (processId: string): void => {
	pending.add(processId);
	version += 1;
	for (const listener of listeners) listener();
};

const subscribe = (listener: () => void): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

const getVersion = (): number => version;

const consumeSetupReveal = (processId: string): boolean =>
	pending.delete(processId);

// The whole reveal, however long the planner wrote, because the campaign card waits behind it.
// Nothing caps `intro_message` — a real one runs past 500 characters — so the LENGTH of each
// step varies and the number of steps does not. A per-character delay cannot do this: with a
// floor of even a few milliseconds, 500 characters runs for six seconds, which is long enough
// for an owner to think the campaign failed to arrive.
const REVEAL_MS = 2200;
const TICK_MS = 25;

/**
 * The campaign setup thread's opening words, revealed as if the agent were typing them.
 *
 * Returns the part revealed so far while a reveal is running, and `null` at every other time —
 * before it starts, after it finishes, and when the owner never pressed for it. So a caller
 * reads `revealed ?? message.content` and always renders the whole text unless a reveal is
 * actually in flight.
 *
 * The words are already stored when the thread is built, so there is no stream behind this and
 * nothing to wait for. It exists because a thread that arrives complete reads as a document,
 * and this one is the agent talking.
 */
export function useSetupIntroReveal(
	processId: string | null,
	fullText: string,
	messageCount: number,
): string | null {
	const pressVersion = useSyncExternalStore(subscribe, getVersion, getVersion);
	const [revealed, setRevealed] = useState<string | null>(null);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const startedFor = useRef<string | null>(null);
	const countAtStart = useRef(0);

	useEffect(() => {
		if (!processId || !fullText) return;
		// Guarded per process, because this effect re-runs whenever the thread gains a message.
		if (startedFor.current === processId) return;
		if (!consumeSetupReveal(processId)) return;
		startedFor.current = processId;
		countAtStart.current = messageCount;

		if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

		const perTick = Math.max(
			1,
			Math.ceil(fullText.length / Math.max(1, REVEAL_MS / TICK_MS)),
		);
		// Seeded with the first characters: an empty agent message renders nothing at all, so
		// starting at zero would show a gap where the words are about to be.
		let cut = perTick;
		setRevealed(fullText.slice(0, cut));
		timerRef.current = setInterval(() => {
			cut += perTick;
			if (cut >= fullText.length) {
				setRevealed(null);
				if (timerRef.current) clearInterval(timerRef.current);
				timerRef.current = null;
				return;
			}
			setRevealed(fullText.slice(0, cut));
		}, TICK_MS);

		return () => {
			if (timerRef.current) clearInterval(timerRef.current);
			timerRef.current = null;
			setRevealed(null);
		};
	}, [processId, fullText, messageCount, pressVersion]);

	useEffect(() => {
		// The owner has spoken, so the agent stops typing over them and shows the rest at once.
		if (startedFor.current === null) return;
		if (messageCount === countAtStart.current) return;
		if (timerRef.current) clearInterval(timerRef.current);
		timerRef.current = null;
		setRevealed(null);
	}, [messageCount]);

	return revealed;
}
